import { apiClient } from './api';
import type {
    DriverActiveShiftResponse,
    EndDriverShiftPayload,
} from '../types';

type DriverShiftApiError = {
    message?: string;
    error_code?: string;
    errors?: Record<string, string[]>;
    status?: number;
};

function firstErrorCode(error: DriverShiftApiError): string | null {
    if (typeof error.error_code === 'string' && error.error_code.trim() !== '') {
        return error.error_code;
    }

    const errorCodes = error.errors?.error_code;
    return Array.isArray(errorCodes) && typeof errorCodes[0] === 'string' ? errorCodes[0] : null;
}

function translateStartShiftError(error: unknown): never {
    const apiError = error as DriverShiftApiError;
    const errorCode = firstErrorCode(apiError);

    if (errorCode === 'CARTA_PORTE_NOT_STAMPED') {
        throw new Error('Tu empresa todavía debe timbrar la Carta Porte antes de iniciar la jornada.');
    }

    if (errorCode === 'DRIVER_SHIFT_ASSIGNMENT_REQUIRED') {
        throw new Error('Todavía no tienes una asignación disponible para iniciar jornada.');
    }

    if (errorCode === 'DRIVER_SHIFT_NOT_READY') {
        throw new Error(apiError.message?.trim() || 'La jornada asignada todavía no está lista para iniciar.');
    }

    throw error;
}

export const driverShiftService = {
    getCurrentShift: async (): Promise<DriverActiveShiftResponse> => {
        return apiClient.get<DriverActiveShiftResponse>('/api/driver/shifts/current', true);
    },

    startCurrentShift: async (): Promise<DriverActiveShiftResponse> => {
        try {
            return await apiClient.post<DriverActiveShiftResponse>('/api/driver/shifts/current/start', {}, true);
        } catch (error) {
            translateStartShiftError(error);
        }
    },

    pauseMatching: async (): Promise<DriverActiveShiftResponse> => {
        return apiClient.post<DriverActiveShiftResponse>('/api/driver/shifts/current/pause-matching', {}, true);
    },

    resumeMatching: async (): Promise<DriverActiveShiftResponse> => {
        return apiClient.post<DriverActiveShiftResponse>('/api/driver/shifts/current/resume-matching', {}, true);
    },

    endShift: async (payload: EndDriverShiftPayload): Promise<DriverActiveShiftResponse> => {
        return apiClient.post<DriverActiveShiftResponse>('/api/driver/shifts/current/end', payload, true);
    },
};
